// The Navo24 workspace opens a service it owns in that service's own app by passing the signed-in session in
// the URL FRAGMENT: `<site>/dashboard#sb_session=<encodeURIComponent(btoa(JSON.stringify({access_token,
// refresh_token})))>` (trackingmcp apps/web ServiceUpsell.tsx, openInOwnApp). A fragment never reaches a
// server or a log. This reads it back. It only parses; the page decides what to do with the tokens, and
// removes them from the address bar straight after.

export interface Handoff {
  access_token: string
  refresh_token: string
}

const KEY = 'sb_session'

// A JWT is three base64url parts. This is a shape check, not verification: Supabase verifies the token
// when setSession uses it, and a forged one is simply refused there.
const looksLikeJwt = (t: unknown): t is string =>
  typeof t === 'string' && t.length < 8192 && /^[\w-]+\.[\w-]+\.[\w-]+$/.test(t)

/** Parse a location.hash; null when it carries no handoff or a malformed one. Never throws. */
export function parseHandoff(hash: string): Handoff | null {
  try {
    const raw = new URLSearchParams(hash.replace(/^#/, '')).get(KEY)
    if (!raw) return null
    // URLSearchParams turns a literal '+' into a space; base64 has no spaces, so put them back.
    const json = atob(raw.replace(/ /g, '+'))
    const v = JSON.parse(json) as { access_token?: unknown; refresh_token?: unknown }
    if (!looksLikeJwt(v.access_token)) return null
    if (typeof v.refresh_token !== 'string' || !v.refresh_token || v.refresh_token.length > 1024)
      return null
    return { access_token: v.access_token, refresh_token: v.refresh_token }
  } catch {
    return null
  }
}

/** The same URL with the handoff removed, ready for history.replaceState. Other fragment keys are kept. */
export function withoutHandoff(pathname: string, search: string, hash: string): string {
  const p = new URLSearchParams(hash.replace(/^#/, ''))
  p.delete(KEY)
  const rest = p.toString()
  return `${pathname}${search}${rest ? `#${rest}` : ''}`
}
